import * as React from 'react';

import { ActivityStatus } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';
import { useSelector } from 'react-redux';

import { FeatureUseEventAdapter } from '~analytics/adapters/FeatureUseEventAdapter';
import { FinishRoute } from '~analytics/types';
import { SmallButton } from '~shared/components/buttons';
import {
  IconFinish,
  IconPause,
  IconResume,
  IconStudents,
} from '~shared/components/icons/standard';
import { Spinner } from '~shared/components/Spinner';
import { toast } from '~shared/components/Toast';
import { ActivityFinishConfirmModal } from '~teacher/components/ActivityResult/modals/ActivityFinishConfirmModal';
import { InviteStudentDrawer } from '~teacher/components/ActivityResult/modals/InviteStudentDrawer/InviteStudentDrawer';
import { useInviteStudentsDrawerDispatch } from '~teacher/components/ActivityResult/modals/InviteStudentDrawer/InviteStudentDrawerContext';
import { ErrorDialog } from '~teacher/components/ErrorDialog';
import { PauseActivityPaywall } from '~teacher/components/PauseActivityPaywall';
import { PaywallProvider } from '~teacher/components/PaywallDialog';
import { useCopyAllToReports } from '~teacher/components/ResultsGrid/atoms/useCopyAllToReports';
import { ResultsHeader } from '~teacher/components/ResultsHeader';
import {
  useActivityByInstanceId,
  useFinishActivity,
  useToggleActivityStatus,
} from '~teacher/hook';
import { ActivitySettingsCamelCase } from '~teacher/models/activities';
import {
  selectCurrentRoomId,
  selectCurrentRoomIsShowbie,
} from '~teacher/slices/teacher/teacherSelector';
import { proCheck } from '~teacher/util/proCheck';

import { assertUnreachable } from '../../../util/assertUnreachable';

interface Props {
  activityInstanceId: number;
  activitySettings: ActivitySettingsCamelCase;
  title: string;
  quizStandard?: string;
  startDate?: string;
  onActivityFinished?: () => void;
}

export function QuizResultsHeaderInProgress({
  activityInstanceId,
  activitySettings,
  title,
  quizStandard,
  startDate,
  onActivityFinished,
}: Props) {
  const isShowbieRoom: boolean = useSelector(selectCurrentRoomIsShowbie);

  const roomId = useSelector(selectCurrentRoomId);
  const activityData = useActivityByInstanceId({
    activityInstanceId,
    pubnub: window.pubnub,
  });
  const isActivityPaused =
    activityData?.data?.activity.state === ActivityStatus.Paused;

  const copyAllToReports = useCopyAllToReports();

  const dispatch = useInviteStudentsDrawerDispatch();

  const [isFinishModalOpen, setIsFinishModalOpen] = React.useState(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = React.useState(false);

  const [isPausePaywallOpen, setIsPausePaywallOpen] = React.useState(false);
  const [isTogglingActivityStatus, setIsTogglingActivityStatus] =
    React.useState(false);

  const { mutateAsync: finishActivity } = useFinishActivity();
  const pauseActivityMutation = useToggleActivityStatus();

  const onModalCloseClick = () => {
    setIsFinishModalOpen(false);
  };
  const onFinishActivityClick = () => {
    setIsFinishModalOpen(true);
  };
  const handleFinishActivity = React.useCallback(() => {
    copyAllToReports();
    finishActivity({
      activityInstanceId: activityInstanceId,
      finishRoute: FinishRoute.ResultsPage,
    })
      .then(() => {
        setIsFinishModalOpen(false);
        onActivityFinished?.();
      })
      .catch(() => {
        setIsFinishModalOpen(false);
        setIsErrorDialogOpen(true);
      });
  }, [
    activityInstanceId,
    copyAllToReports,
    finishActivity,
    onActivityFinished,
  ]);

  const toggleActivityStatus = React.useCallback(() => {
    if (!proCheck.canPauseActivity()) {
      setIsPausePaywallOpen(true);
      return;
    }

    setIsTogglingActivityStatus(true);

    pauseActivityMutation.mutate(
      {
        activityInstanceId: activityInstanceId,
      },
      {
        onSuccess: ({ data: { state } }) => {
          setIsTogglingActivityStatus(false);
          switch (state) {
            case ActivityStatus.Paused:
              toast(
                <div className="flex items-center">
                  <IconPause className="text-white mr-2" />
                  {translate('Activity Paused')}
                </div>,
                { disableClose: true, duration: 1000 }
              );
              break;
            case ActivityStatus.Running:
              toast(
                <div className="flex items-center">
                  <IconResume className="text-white mr-2" />
                  {translate('Activity Resumed')}
                </div>,
                { disableClose: true, duration: 1000 }
              );
              break;
            default:
              assertUnreachable(state as never);
          }
        },
        onError: async () => {
          setIsTogglingActivityStatus(false);
          if (isActivityPaused) {
            toast.critical(
              translate('An error occurred while trying to resume')
            );
          } else {
            toast.critical(
              translate('An error occurred while trying to pause')
            );
          }
        },
      }
    );
  }, [activityInstanceId, isActivityPaused, pauseActivityMutation]);

  const openInviteStudentsDrawer = () => {
    new FeatureUseEventAdapter({
      feature: 'invite_student_drawer',
      action: 'open',
      source: 'activity_results_header',
    }).sendAnalytics();
    dispatch('Open');
  };

  return (
    <>
      <PaywallProvider>
        <div className="flex flex-col">
          <ResultsHeader
            title={title}
            standard={quizStandard}
            startDate={startDate}
            endDate={null}
            activitySettings={activitySettings}
          >
            <SmallButton
              appearance="default"
              onClick={() => {
                if (isTogglingActivityStatus) {
                  return;
                }
                toggleActivityStatus();
              }}
              isDisabled={isTogglingActivityStatus}
              isLoading={isTogglingActivityStatus}
              className="flex-grow max-w-80"
            >
              {(() => {
                if (isTogglingActivityStatus) {
                  return (
                    <span className="min-w-32">
                      <Spinner size="small" />
                    </span>
                  );
                } else if (isActivityPaused) {
                  return (
                    <>
                      <IconResume className="mr-2" />
                      {translate('Resume')}
                    </>
                  );
                } else {
                  return (
                    <>
                      <IconPause className="mr-2" />
                      {translate('Pause')}
                    </>
                  );
                }
              })()}
            </SmallButton>
            <SmallButton
              appearance="default"
              onClick={onFinishActivityClick}
              className="flex-grow max-w-80"
            >
              <IconFinish size="micro" className="mr-2" />
              {translate('Finish Activity')}
            </SmallButton>
            {isShowbieRoom === false ? (
              <>
                <SmallButton
                  appearance="primary"
                  onClick={openInviteStudentsDrawer}
                  className="flex-grow max-w-80"
                >
                  <IconStudents className="text-white mr-2" />
                  {translate('Invite Students')}
                </SmallButton>
              </>
            ) : null}
          </ResultsHeader>
        </div>
      </PaywallProvider>
      <ActivityFinishConfirmModal
        open={isFinishModalOpen}
        onClose={onModalCloseClick}
        onConfirmClick={handleFinishActivity}
      />

      <ErrorDialog
        open={isErrorDialogOpen}
        onClose={() => {
          setIsErrorDialogOpen(false);
        }}
      />
      <InviteStudentDrawer roomId={roomId} />
      <PauseActivityPaywall
        open={isPausePaywallOpen}
        onClose={() => setIsPausePaywallOpen(false)}
      />
    </>
  );
}
